import React, { useState, useEffect } from 'react';
import {
    Mail,
    Phone,
    Linkedin,
    MapPin,
    Clock,
    CheckCircle,
    AlertCircle,
    Loader2,
    Code,
    Layers,
    Cpu,
    Film,
    Camera,
    Search,
    Zap,
    ArrowRight
} from 'lucide-react';
import SEOLayout from '../components/layout/SEOLayout';

const Contact = () => {
    const [contactData, setContactData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        subject: '',
        project_type: '',
        investment_range: '',
        timeline: '',
        message: ''
    });
    const [formStatus, setFormStatus] = useState({
        isSubmitting: false,
        isSubmitted: false,
        error: null
    });
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        const fetchContactData = async () => {
            try {
                const response = await fetch('/api/content/contact');
                const data = await response.json();
                setContactData(data);
            } catch (error) {
                console.error('Error loading contact data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchContactData();
    }, []);

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!formData.subject.trim()) {
            errors.subject = 'Project focus is required';
        }

        if (!formData.message.trim()) {
            errors.message = 'Challenge description is required';
        } else if (formData.message.trim().length < 20) {
            errors.message = 'Please provide more detail about your challenge (minimum 20 characters)';
        }

        return errors;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm();
        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            const firstErrorField = Object.keys(errors)[0];
            const element = document.querySelector(`[name="${firstErrorField}"]`);
            if (element) {
                element.focus();
            }
            return;
        }

        setFormStatus({ isSubmitting: true, isSubmitted: false, error: null });

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setFormStatus({
                    isSubmitting: false,
                    isSubmitted: true,
                    error: null
                });

                setFormData({
                    name: '',
                    email: '',
                    company: '',
                    subject: '',
                    project_type: '',
                    investment_range: '',
                    timeline: '',
                    message: ''
                });

                const announcement = document.createElement('div');
                announcement.setAttribute('aria-live', 'polite');
                announcement.setAttribute('aria-atomic', 'true');
                announcement.className = 'sr-only';
                announcement.textContent = 'Message sent successfully!';
                document.body.appendChild(announcement);
                setTimeout(() => document.body.removeChild(announcement), 1000);

            } else {
                throw new Error(result.message || 'Failed to send message');
            }
        } catch (error) {
            setFormStatus({
                isSubmitting: false,
                isSubmitted: false,
                error: error.message
            });
        }
    };

    const iconMap = {
        Mail, Phone, Linkedin, MapPin, Clock, Code, Layers, Cpu, Film, Camera, Search
    };

    if (loading) {
        return (
            <SEOLayout>
                <div className="min-h-screen flex items-center justify-center bg-gray-900">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-400"></div>
                </div>
            </SEOLayout>
        );
    }

    return (
        <SEOLayout
            title="Contact"
            description={contactData?.hero?.description}
            keywords="Baraqu LLC contact, technology solutions, systematic innovation consulting"
        >
            {/* Hero Section */}
            <section className="relative bg-gray-900 overflow-hidden" aria-labelledby="contact-heading">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <div className="text-center">
                        <div className="flex justify-center mb-6">
                            <Zap className="h-12 w-12 text-blue-400" />
                        </div>
                        <h1 id="contact-heading" className="text-5xl md:text-6xl font-light text-white mb-6">
                            {contactData?.hero?.title}
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
                            {contactData?.hero?.subtitle}
                        </p>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                            {contactData?.hero?.description}
                        </p>
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto py-20 px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                    {/* Contact Information */}
                    <div className="lg:col-span-1">
                        <div className="bg-white shadow-sm rounded-lg p-8 border border-gray-200">
                            <h2 className="text-2xl font-medium text-gray-900 mb-8">Let's Connect</h2>

                            {/* Contact Methods */}
                            <div className="space-y-6">
                                {contactData?.contact_methods?.map((method) => {
                                    const IconComponent = iconMap[method.icon] || Mail;
                                    return (
                                        <div key={method.type} className="flex items-start">
                                            <div className="flex-shrink-0">
                                                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-blue-100">
                                                    <IconComponent className="h-5 w-5 text-blue-600" aria-hidden="true" />
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <h3 className="text-lg font-medium text-gray-900">
                                                    {method.label}
                                                </h3>
                                                {method.type === 'email' ? (
                                                    <a
                                                        href={`mailto:${method.value}`}
                                                        className="text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded transition-colors duration-200"
                                                        aria-label={`Email ${method.value}`}
                                                    >
                                                        {method.value}
                                                    </a>
                                                ) : method.type === 'phone' ? (
                                                    <a
                                                        href={`tel:${method.value}`}
                                                        className="text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded transition-colors duration-200"
                                                        aria-label={`Call ${method.value}`}
                                                    >
                                                        {method.value}
                                                    </a>
                                                ) : method.type === 'linkedin' ? (
                                                    <a
                                                        href={method.value}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded transition-colors duration-200"
                                                        aria-label="Connect on LinkedIn"
                                                    >
                                                        LinkedIn Profile
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-700">{method.value}</span>
                                                )}
                                                <p className="text-sm text-gray-500 mt-1">
                                                    {method.description}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Response Commitment */}
                            {contactData?.response_commitment && (
                                <div className="mt-8 pt-8 border-t border-gray-200">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        {contactData.response_commitment.title}
                                    </h3>
                                    <div className="space-y-3">
                                        {contactData.response_commitment.details?.map((detail, index) => (
                                            <div key={index} className="flex items-start space-x-3">
                                                <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <p className="font-medium text-gray-900">{detail.timeframe}</p>
                                                    <p className="text-sm text-gray-600">{detail.response}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {contactData.response_commitment.note && (
                                        <p className="text-sm text-gray-500 mt-4 italic">
                                            {contactData.response_commitment.note}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="lg:col-span-2">
                        <div className="bg-white shadow-sm rounded-lg p-8 border border-gray-200">
                            <h2 className="text-2xl font-medium text-gray-900 mb-2">
                                {contactData?.form?.title}
                            </h2>
                            <p className="text-gray-600 mb-8">
                                {contactData?.form?.subtitle}
                            </p>

                            {/* Success Message */}
                            {formStatus.isSubmitted && (
                                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" role="alert">
                                    <div className="flex items-center">
                                        <CheckCircle className="h-5 w-5 text-blue-600 mr-2" aria-hidden="true" />
                                        <p className="text-blue-800">
                                            {contactData?.form?.success_message}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Error Message */}
                            {formStatus.error && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                                    <div className="flex items-center">
                                        <AlertCircle className="h-5 w-5 text-red-600 mr-2" aria-hidden="true" />
                                        <p className="text-red-800">{formStatus.error}</p>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    {/* Name Field */}
                                    <div className="sm:col-span-1">
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                            Your Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            id="name"
                                            required
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className={`block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                                                formErrors.name ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                            placeholder="Full name"
                                            aria-describedby={formErrors.name ? "name-error" : undefined}
                                            aria-invalid={formErrors.name ? 'true' : 'false'}
                                        />
                                        {formErrors.name && (
                                            <p id="name-error" className="mt-1 text-sm text-red-600" role="alert">
                                                {formErrors.name}
                                            </p>
                                        )}
                                    </div>

                                    {/* Email Field */}
                                    <div className="sm:col-span-1">
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            id="email"
                                            required
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className={`block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                                                formErrors.email ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                            placeholder="your@company.com"
                                            aria-describedby={formErrors.email ? "email-error" : undefined}
                                            aria-invalid={formErrors.email ? 'true' : 'false'}
                                        />
                                        {formErrors.email && (
                                            <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">
                                                {formErrors.email}
                                            </p>
                                        )}
                                    </div>

                                    {/* Company Field */}
                                    <div className="sm:col-span-1">
                                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                                            Organization
                                        </label>
                                        <input
                                            type="text"
                                            name="company"
                                            id="company"
                                            value={formData.company}
                                            onChange={handleInputChange}
                                            className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                            placeholder="Your company or institution"
                                        />
                                    </div>

                                    {/* Project Type Field */}
                                    <div className="sm:col-span-1">
                                        <label htmlFor="project_type" className="block text-sm font-medium text-gray-700 mb-2">
                                            Solution Category
                                        </label>
                                        <select
                                            name="project_type"
                                            id="project_type"
                                            value={formData.project_type}
                                            onChange={handleInputChange}
                                            className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                        >
                                            {contactData?.form?.fields?.find(f => f.name === 'project_type')?.options?.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Subject Field */}
                                    <div className="sm:col-span-2">
                                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                                            Project Focus <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="subject"
                                            id="subject"
                                            required
                                            value={formData.subject}
                                            onChange={handleInputChange}
                                            className={`block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                                                formErrors.subject ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                            placeholder="Brief description of your challenge or opportunity"
                                            aria-describedby={formErrors.subject ? "subject-error" : undefined}
                                            aria-invalid={formErrors.subject ? 'true' : 'false'}
                                        />
                                        {formErrors.subject && (
                                            <p id="subject-error" className="mt-1 text-sm text-red-600" role="alert">
                                                {formErrors.subject}
                                            </p>
                                        )}
                                    </div>

                                    {/* Investment Range Field */}
                                    <div className="sm:col-span-1">
                                        <label htmlFor="investment_range" className="block text-sm font-medium text-gray-700 mb-2">
                                            Investment Range
                                        </label>
                                        <select
                                            name="investment_range"
                                            id="investment_range"
                                            value={formData.investment_range}
                                            onChange={handleInputChange}
                                            className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                        >
                                            {contactData?.form?.fields?.find(f => f.name === 'investment_range')?.options?.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Timeline Field */}
                                    <div className="sm:col-span-1">
                                        <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                                            Timeline
                                        </label>
                                        <select
                                            name="timeline"
                                            id="timeline"
                                            value={formData.timeline}
                                            onChange={handleInputChange}
                                            className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                        >
                                            {contactData?.form?.fields?.find(f => f.name === 'timeline')?.options?.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Message Field */}
                                    <div className="sm:col-span-2">
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                            Challenge Description <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            name="message"
                                            id="message"
                                            rows={6}
                                            required
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            className={`block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                                                formErrors.message ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                            placeholder="Describe your current challenge, what you've tried, and what success looks like. The more context you provide, the better we can tailor our initial recommendations."
                                            aria-describedby={formErrors.message ? "message-error" : undefined}
                                            aria-invalid={formErrors.message ? 'true' : 'false'}
                                        />
                                        {formErrors.message && (
                                            <p id="message-error" className="mt-1 text-sm text-red-600" role="alert">
                                                {formErrors.message}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="mt-8">
                                    <button
                                        type="submit"
                                        disabled={formStatus.isSubmitting}
                                        className="inline-flex justify-center items-center px-8 py-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                    >
                                        {formStatus.isSubmitting ? (
                                            <>
                                                <Loader2 className="animate-spin h-5 w-5 mr-2" aria-hidden="true" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                {contactData?.form?.submit_text || "Send Message"}
                                                <ArrowRight className="ml-2 h-5 w-5" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Solutions Overview */}
                {contactData?.solutions && (
                    <div className="mt-20">
                        <h2 className="text-3xl font-light text-gray-900 text-center mb-4">Solution Capabilities</h2>
                        <p className="text-xl text-gray-600 text-center mb-12">Systematic innovation across six specialized domains</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {contactData.solutions.map((solution, index) => {
                                const IconComponent = iconMap[solution.icon] || iconMap.Code;
                                return (
                                    <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors duration-200">
                                        <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-blue-100 mb-4">
                                            <IconComponent className="h-6 w-6 text-blue-600" aria-hidden="true" />
                                        </div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">{solution.title}</h3>
                                        <p className="text-gray-600">{solution.description}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </SEOLayout>
    );
};

export default Contact;